import { ref, computed, watch, onBeforeUnmount, markRaw, reactive, shallowRef } from "vue";
import { useStore } from "vuex";
import { useRoute, useRouter } from "vue-router";
import { useQueryClient } from "@tanstack/vue-query";
import { useToast } from "@/composables/ui/useToast";
import { useIncomingCall } from "@/composables/calls/useIncomingCall";
import { useGlobalFollowupModal } from "@/composables/bucket/useGlobalFollowupModal";
import { useAIAssistant } from "@/composables/calls/useAIAssistant";
import { useActiveDialerSession } from "./useActiveDialerSession";
import { useSetDialerPresence } from "./useDialerPresence";
import {
  usePauseDialerSession,
  useResumeDialerSession,
  useEndDialerSession,
} from "./useDialerSessionControls";
import { useDialerWrapup } from "./useDialerWrapup";
import { useDialerSocketListeners } from "./useDialerSocketListeners";
import { DIALER_SESSION_ROUTE } from "@/router/routes";
import Twillio from "@/services/twilio/twilio";

export const activeCallStates = ["connecting", "ringing", "in-call", "reconnecting"];

// The power dialer runs in the background for the whole app, not just while
// the /dialer page is open: it lives in PowerDialerHost (mounted once in
// App.vue), which calls createPowerDialer() and publishes the controller here
// so the /dialer page and the layout can read it. Null while no one is
// logged in (the host isn't mounted then).
const currentPowerDialer = shallowRef<PowerDialerController | null>(null);

export function usePowerDialer() {
  return currentPowerDialer;
}

export type PowerDialerController = ReturnType<typeof createPowerDialer>;

// Only for PowerDialerHost — must be called exactly once, from a component's
// setup, since it registers socket listeners and lifecycle hooks.
export function createPowerDialer() {
  const store = useStore();
  const route = useRoute();
  const router = useRouter();
  const $toast = useToast();
  const queryClient = useQueryClient();

  const account = computed(() => store.getters.account?.data);

  const { data: activeSession, isLoading: isLoadingActiveSession } = useActiveDialerSession();
  const { mutateAsync: setPresence, isPending: isSettingPresence } = useSetDialerPresence();
  const { mutateAsync: pauseSession, isPending: isPausingSession } = usePauseDialerSession();
  const { mutateAsync: resumeSession, isPending: isResumingSession } = useResumeDialerSession();
  const { mutateAsync: endSession, isPending: isEndingSession } = useEndDialerSession();
  const { mutateAsync: submitWrapup, isPending: isSubmittingWrapup } = useDialerWrapup();
  const { setOutboundCallSid, clearOutboundCallSid } = useIncomingCall();
  const {
    openFollowupModal,
    isModalOpen: isFollowupModalOpenGlobal,
    openContactId: openFollowupContactId,
  } = useGlobalFollowupModal();
  // The contact whose follow-up modal the dialer itself opened last — only
  // that modal is safe to swap out when the next call connects.
  let lastDialerFollowupContactId: string | null = null;

  const hasActiveSession = computed(() => !!activeSession.value?.session);
  const sessionStatus = computed(() => activeSession.value?.session?.status || "active");

  const enableTranscription = computed(() => account.value?.enableTranscription !== false);
  const enableAISummary = computed(
    () => enableTranscription.value && account.value?.enableAISummary !== false
  );

  // AI call assistant, set on the /dialer page (per session, changeable between
  // calls) and read here as each call connects. It runs off the
  // live transcript, so it's a no-op with transcription turned off.
  const {
    prepareContext: prepareAIContext,
    enableAssistant: enableAIAssistant,
    disableAssistant: disableAIAssistant,
  } = useAIAssistant();
  const sessionAIAssistantEnabled = computed(
    () => !!activeSession.value?.session?.enableAIAssistant && enableTranscription.value
  );
  const sessionAIIcpId = computed(() => activeSession.value?.session?.icpId || undefined);
  // Whether this dialer turned the assistant on for the current call, so
  // tearing the call down only touches the assistant state it owns.
  let aiAssistantOwnedByCall = false;

  const currentQueueItem = ref<any>(null);
  const currentDialerCallListContactId = ref<string | null>(null);
  const wrapupTarget = ref<{ dialerCallListContactId: string } | null>(null);
  const selectedDisposition = ref<any>(null);
  const wrapupNotes = ref("");
  const showEndSessionConfirm = ref(false);

  // --- Call state (driven by CallSessionModal, the same call UI used elsewhere in the app) ---
  const callStatus = ref("ready");
  const to = ref("");
  const isMuted = ref(false);
  const saveRecording = ref(true);
  const currentCall = ref<any>(null);
  const currentCallSid = ref<string | null>(null);
  const parentCallSid = ref<string | null>(null);
  const microphones = ref<any[]>([]);
  const speakers = ref<any[]>([]);
  const selectedMicId = ref("");
  const selectedSpeakerId = ref("");
  const callDuration = ref(0);
  let callTimer: ReturnType<typeof setInterval> | null = null;

  // True once a real person is confirmed on the line. The dialer runs in the
  // background while the rep works elsewhere in the app, so nothing pops up
  // while a call is only dialing/ringing (or reached a voicemail the backend
  // is about to hang up and auto-skip) — the call and follow-up modals only
  // open once this flips, driven by the backend's dialer:call_connected push
  // (which knows Twilio's AMD result; the browser's own "accept" event can't
  // tell a human from an answering machine).
  const isHumanConnected = ref(false);

  // Safety net in case dialer:call_connected is ever dropped: if the call has
  // been bridged (browser "accept") for this long and is still up, assume a
  // human — machine answers are hung up by the backend well within this.
  const CONNECT_FALLBACK_MS = 10000;
  let connectFallbackId: ReturnType<typeof setTimeout> | null = null;

  function clearConnectFallback() {
    if (connectFallbackId) {
      clearTimeout(connectFallbackId);
      connectFallbackId = null;
    }
  }

  // True once the rep has explicitly clicked "Start Session" this page load, or
  // (after a refresh mid-session) if the queue already shows attempted calls.
  const sessionStartedOverride = ref(false);
  const hasStarted = computed(() => {
    if (sessionStartedOverride.value) return true;
    const summary = activeSession.value?.summary;
    if (!summary) return false;
    return summary.dialed + summary.completed + summary.skipped > 0;
  });

  // The backend's per-item `status` can lag behind the disposition actually being
  // saved (submitting a wrap-up records `disposition` on the queue item without
  // necessarily flipping `status` to "completed" in the summary it returns), which
  // made the banner's "completed" count undercount contacts that were fully
  // handled even though the queue table already showed their saved disposition.
  // Recompute the counts from the queue itself — the more reliable source — for
  // display, rather than trusting `summary.completed`/`summary.dialed` as-is.
  // Skipped rows are excluded from "completed" even though they carry a
  // disposition — the backend auto-assigns one (No Answer, Voicemail, …) when
  // it skips a call, which otherwise counted the same row as both.
  const displaySummary = computed(() => {
    const summary = activeSession.value?.summary;
    if (!summary) return summary;
    const queue = activeSession.value?.queue ?? [];
    const completed = queue.filter(
      (item) => item.status !== "skipped" && (item.status === "completed" || !!item.disposition)
    ).length;
    const skipped = queue.filter((item) => item.status === "skipped").length;
    const pending = queue.filter((item) => item.status === "pending").length;
    const dialed = Math.max(summary.total - completed - skipped - pending, 0);
    return { ...summary, completed, skipped, pending, dialed };
  });

  // After the browser leg disconnects, the client doesn't yet know whether the
  // call was auto-skipped (voicemail/no-answer/busy/disconnected) or needs a
  // manual disposition — only the backend's webhook (which sees the real
  // CallStatus/AMD result) knows that. This gates the wait for whichever of
  // dialer:auto_skipped / dialer:wrapup_needed arrives, with a timeout fallback
  // to the old "always open the wrap-up modal" behavior in case a webhook is
  // ever dropped, so the rep is never stuck.
  const OUTCOME_WAIT_TIMEOUT_MS = 6000;
  const awaitingCallOutcome = ref(false);
  let outcomeTimeoutId: ReturnType<typeof setTimeout> | null = null;

  function clearOutcomeTimeout() {
    if (outcomeTimeoutId) {
      clearTimeout(outcomeTimeoutId);
      outcomeTimeoutId = null;
    }
    awaitingCallOutcome.value = false;
  }

  // Calls no longer auto-advance — the rep must explicitly click "Next Call" /
  // "Start Session" (setPresence to "available" is what actually tells the
  // backend to push the next dialer:next_call). This gates when that action is
  // available: not mid-call, not mid-wrapup, session not paused, and there's
  // actually someone left to call.
  const canTriggerNextCall = computed(() => {
    const pending = displaySummary.value?.pending ?? 0;
    return (
      pending > 0 &&
      sessionStatus.value !== "paused" &&
      !activeCallStates.includes(callStatus.value) &&
      !wrapupTarget.value &&
      !awaitingCallOutcome.value
    );
  });

  const showCallModal = computed(
    () => isHumanConnected.value && activeCallStates.includes(callStatus.value)
  );

  const AUTO_SKIP_REASON_LABELS: Record<string, string> = {
    voicemail: "Voicemail",
    "no-answer": "No answer",
    busy: "Line busy",
    failed: "Number disconnected",
  };

  // Armed each time a dialer call is placed; once both the disposition is
  // saved (canTriggerNextCall) and the contact's follow-up modal is closed —
  // whichever finishes last — the next call is triggered automatically instead
  // of waiting for the rep to click "Next Call".
  const dialerAutoAdvancePending = ref(false);

  // Tallied live from this session's call events (not derived from the
  // backend summary, which has no notion of "answered" — only
  // dialed/completed/skipped). Feeds the end-of-session summary below. Being
  // purely client-side, a page refresh mid-session resets these; that's an
  // accepted gap rather than something worth persisting for.
  const sessionDialedCount = ref(0);
  const sessionAnsweredCount = ref(0);
  const showSessionSummary = ref(false);
  const sessionSummaryStats = ref<{
    totalCalls: number;
    answeredCalls: number;
    connectionRate: number;
    totalContacts: number;
    completedContacts: number;
    completionRate: number;
  } | null>(null);

  const currentContactName = computed(() => {
    const contact = currentQueueItem.value?.contact;
    if (!contact) return undefined;
    return `${contact.fname || ""} ${contact.lname || ""}`.trim();
  });

  const formattedCallTimer = computed(() => {
    const hours = String(Math.floor(callDuration.value / 3600)).padStart(2, "0");
    const minutes = String(Math.floor((callDuration.value % 3600) / 60)).padStart(2, "0");
    const seconds = String(callDuration.value % 60).padStart(2, "0");
    return `${hours}:${minutes}:${seconds}`;
  });

  // Only show a duration on the banner once actually connected — "00:00:00"
  // while merely ringing/connecting isn't meaningful.
  const bannerCallDuration = computed(() =>
    callStatus.value === "in-call" ? formattedCallTimer.value : ""
  );

  function startCallTimer() {
    callDuration.value = 0;
    if (callTimer) clearInterval(callTimer);
    callTimer = setInterval(() => {
      callDuration.value++;
    }, 1000);
  }

  function stopCallTimer() {
    if (callTimer) {
      clearInterval(callTimer);
      callTimer = null;
    }
  }

  async function requestNextCall() {
    dialerAutoAdvancePending.value = false;
    try {
      await setPresence({ status: "available" });
      sessionStartedOverride.value = true;
    } catch (error: any) {
      $toast.error(error?.response?.data?.message || "Failed to start the next call.");
    }
  }

  // Fires once the rep has both saved the call's disposition (canTriggerNextCall)
  // and closed/saved the contact's follow-up modal — whichever happens last —
  // so the next contact is dialed without waiting for a manual "Next Call" click.
  const readyForAutoAdvance = computed(
    () =>
      dialerAutoAdvancePending.value && canTriggerNextCall.value && !isFollowupModalOpenGlobal.value
  );

  watch(readyForAutoAdvance, (ready) => {
    if (!ready) return;
    $toast.info("Follow-up saved — calling the next contact...");
    requestNextCall();
  });

  async function pauseSessionHandler() {
    try {
      await pauseSession();
      $toast.success("Dialer session paused.");
    } catch (error: any) {
      $toast.error(error?.response?.data?.message || "Failed to pause dialer session.");
    }
  }

  async function resumeSessionHandler() {
    try {
      await resumeSession();
      $toast.success("Dialer session resumed.");
    } catch (error: any) {
      $toast.error(error?.response?.data?.message || "Failed to resume dialer session.");
    }
  }

  // Twilio + audio devices are only prepared once there's actually a session to
  // run, so merely loading the app never triggers a microphone prompt.
  const twilioReady = ref(false);
  let audioDevicesPrepared = false;

  async function prepareTwilio() {
    if (!account.value?.id) return;
    try {
      await Twillio.initTwilio(`user_${account.value.id}`);
      twilioReady.value = true;
    } catch (error) {
      console.error("Failed to initialize Twilio device:", error);
      $toast.error("Failed to initialize call system. Please refresh the page.");
      return;
    }

    if (audioDevicesPrepared) return;
    audioDevicesPrepared = true;
    try {
      await navigator.mediaDevices.getUserMedia({ audio: true });
      const all = await navigator.mediaDevices.enumerateDevices();
      microphones.value = all
        .filter((d) => d.kind === "audioinput")
        .map((d) => ({ deviceId: d.deviceId, label: d.label || "Microphone" }));
      if (microphones.value.length && !selectedMicId.value) {
        selectedMicId.value = microphones.value[0].deviceId;
      }
      speakers.value = Twillio.getSpeakers();
      if (speakers.value.length && !selectedSpeakerId.value) {
        selectedSpeakerId.value = speakers.value[0].deviceId;
      }
    } catch (error) {
      console.warn("[PowerDialer] Failed to prepare audio devices:", error);
    }
  }

  watch(
    hasActiveSession,
    (has) => {
      if (has && !twilioReady.value) prepareTwilio();
    },
    { immediate: true }
  );

  // Set when the rep arrives on the dialer page via the "Resume Power Dial"
  // button on the Contacts/Buckets toolbar (?autoNext=1), instead of a plain
  // page visit — in that case they want to land straight into the next call.
  let autoNextInFlight = false;

  async function maybeAutoStartNextCall() {
    if (autoNextInFlight) return;
    if (route.name !== DIALER_SESSION_ROUTE || route.query.autoNext !== "1") return;
    if (!twilioReady.value || isLoadingActiveSession.value) return;
    if (!activeSession.value) return;

    autoNextInFlight = true;
    // Drop the query param so a refresh or navigating back here later doesn't
    // re-trigger an unwanted auto-dial.
    router.replace({ name: DIALER_SESSION_ROUTE }).catch(() => {});

    try {
      if (sessionStatus.value === "paused") {
        await resumeSession();
      }
      await requestNextCall();
    } catch (error: any) {
      $toast.error(error?.response?.data?.message || "Failed to resume the dialer session.");
    } finally {
      autoNextInFlight = false;
    }
  }

  watch([twilioReady, isLoadingActiveSession, activeSession, () => route.query.autoNext], () => {
    maybeAutoStartNextCall();
  });

  // Snapshotted right before ending, since `endSession()` invalidates the active
  // session query and `activeSession`/`displaySummary` go null shortly after.
  function buildSessionSummary() {
    const summary = displaySummary.value;
    const totalContacts = summary?.total ?? 0;
    const completedContacts = summary?.completed ?? 0;
    // Prefer the live tally; fall back to the backend summary if the app was
    // refreshed mid-session and the local counters lost their count.
    const totalCalls = sessionDialedCount.value || completedContacts + (summary?.dialed ?? 0);
    const answeredCalls = sessionAnsweredCount.value;

    return {
      totalCalls,
      answeredCalls,
      connectionRate: totalCalls > 0 ? Math.round((answeredCalls / totalCalls) * 100) : 0,
      totalContacts,
      completedContacts,
      completionRate: totalContacts > 0 ? Math.round((completedContacts / totalContacts) * 100) : 0,
    };
  }

  async function confirmEndSession() {
    try {
      sessionSummaryStats.value = buildSessionSummary();
      // Ending the session removes the only UI with hangup controls
      // (CallSessionModal) — without this, ending mid-call left the Twilio
      // connection running with no way to hang up.
      teardownActiveCall();
      await endSession();
      currentQueueItem.value = null;
      currentDialerCallListContactId.value = null;
      dialerAutoAdvancePending.value = false;
      wrapupTarget.value = null;
      showSessionSummary.value = true;
      // The session can be ended from the banner on any page — the summary
      // lives on the dialer page, so take the rep there to see it.
      if (route.name !== DIALER_SESSION_ROUTE) {
        router.push({ name: DIALER_SESSION_ROUTE }).catch(() => {});
      }
    } catch (error: any) {
      sessionSummaryStats.value = null;
      $toast.error(error?.response?.data?.message || "Failed to end dialer session.");
    } finally {
      showEndSessionConfirm.value = false;
    }
  }

  function closeSessionSummary() {
    showSessionSummary.value = false;
    sessionSummaryStats.value = null;
    sessionDialedCount.value = 0;
    sessionAnsweredCount.value = 0;
  }

  // This controller lives for the whole app session rather than one visit to
  // the dialer page, so per-session state has to be reset explicitly when a
  // new session starts (e.g. a summary the rep never dismissed, or the
  // "started" flag from the previous session).
  watch(
    () => activeSession.value?.session?.id,
    (newId, oldId) => {
      if (!newId || newId === oldId) return;
      sessionStartedOverride.value = false;
      closeSessionSummary();
    }
  );

  function resetCallLocalState() {
    clearConnectFallback();
    currentCall.value = null;
    currentCallSid.value = null;
    parentCallSid.value = null;
    callStatus.value = "ready";
    isMuted.value = false;
    isHumanConnected.value = false;
    stopCallTimer();
    clearOutboundCallSid();
    if (aiAssistantOwnedByCall) {
      aiAssistantOwnedByCall = false;
      disableAIAssistant();
    }
  }

  // Hangs up whatever call is in progress (if any) and strips its listeners
  // first, so its "disconnect" handler can't fire afterward and open a
  // wrap-up prompt for a call the session no longer cares about. Used when
  // tearing down the whole session (End Session) or the dialer itself
  // (logout) — both remove the only UI with hangup controls, so a live call
  // left behind would be unreachable.
  function teardownActiveCall() {
    clearOutcomeTimeout();
    const call = currentCall.value;
    if (call) {
      try {
        call.removeAllListeners?.();
      } catch (err) {
        console.warn("[PowerDialer] Failed to remove call listeners:", err);
      }
    }
    if (call || activeCallStates.includes(callStatus.value)) {
      Twillio.hangup();
    }
    resetCallLocalState();
  }

  // A call that never actually connected (rejected/canceled/errored) never reaches
  // the wrap-up flow — just clear the local call state so "Next Call" becomes
  // available again. Presence is only ever advanced by an explicit click now.
  function resetCallLocalStateAfterFailure() {
    resetCallLocalState();
    currentQueueItem.value = null;
    currentDialerCallListContactId.value = null;
    // placeCall() sets this true expecting a real disposition to follow — a
    // call that never connected won't get one, so clear it here too, or
    // closing a still-open follow-up modal would trigger an unwanted auto-dial
    // to the next contact.
    dialerAutoAdvancePending.value = false;
  }

  function toggleMute() {
    if (!currentCall.value) return;
    isMuted.value = !isMuted.value;
    currentCall.value.mute(isMuted.value);
  }

  async function toggleRecording() {
    if (!currentCall.value || !currentCallSid.value) {
      $toast.info("No ongoing call");
      return;
    }
    if (!account.value?.allowCallRecording) {
      $toast.info("Please enable recording in settings.");
      return;
    }
    const wasRecording = saveRecording.value;
    saveRecording.value = !wasRecording;
    const sid = parentCallSid.value || currentCallSid.value;
    try {
      if (wasRecording) {
        await Twillio.pauseRecording(sid);
        $toast.info("Recording paused");
      } else {
        await Twillio.resumeRecording(sid);
        $toast.info("Recording resumed");
      }
    } catch (err) {
      saveRecording.value = wasRecording;
      $toast.error("Failed to update recording");
      console.error("[PowerDialer] toggleRecording failed:", err);
    }
  }

  async function selectMicrophone(deviceId: string) {
    try {
      await Twillio.setMicrophone(deviceId);
      selectedMicId.value = deviceId;
      $toast.info("Switched mic!");
    } catch (err) {
      $toast.error("Failed to switch microphone");
      console.error("[PowerDialer] selectMicrophone failed:", err);
    }
  }

  async function selectSpeaker(deviceId: string) {
    try {
      await Twillio.setSpeaker(deviceId);
      selectedSpeakerId.value = deviceId;
      $toast.info("Switched speaker");
    } catch (err) {
      $toast.error("Failed to switch speaker");
      console.error("[PowerDialer] selectSpeaker failed:", err);
    }
  }

  function sendDigit(char: string) {
    try {
      Twillio.sendDigits(char);
    } catch (err) {
      console.error("[PowerDialer] sendDigits failed:", err);
    }
  }

  async function handleTransfer(phoneNumber: string) {
    if (!currentCall.value || !currentCallSid.value) {
      $toast.info("No ongoing call to transfer");
      return;
    }
    const sid = parentCallSid.value || currentCallSid.value;
    try {
      await Twillio.transferCall(sid, phoneNumber);
      $toast.success("Transfer initiated");
      hangupCall();
    } catch (error) {
      console.error("[PowerDialer] Transfer failed:", error);
      $toast.error("Unable to transfer call");
    }
  }

  function hangupCall() {
    Twillio.hangup();
  }

  function handleCloseCallModal() {
    if (activeCallStates.includes(callStatus.value)) {
      hangupCall();
    }
  }

  // A person is on the line — surface the call controls and the contact's
  // follow-up modal over whatever page the rep is currently on. Idempotent, as
  // both the backend push and the local fallback timer can call it.
  function markHumanConnected() {
    if (isHumanConnected.value || !currentCall.value) return;
    clearConnectFallback();
    isHumanConnected.value = true;
    callStatus.value = "in-call";
    startCallTimer();
    sessionAnsweredCount.value++;

    const contact = currentQueueItem.value?.contact;
    if (!contact) return;

    // Only now, not on "accept" — that also fires for voicemail, and enabling
    // marks the call as AI-billable.
    const callSid = parentCallSid.value || currentCallSid.value;
    if (sessionAIAssistantEnabled.value && callSid) {
      aiAssistantOwnedByCall = true;
      enableAIAssistant(callSid, contact.id, callSid, sessionAIIcpId.value).catch((err) => {
        console.warn("[PowerDialer] AI assistant enable failed:", err);
      });
    }

    // Already showing this contact's follow-up — leave it (and whatever the
    // rep has typed into it) alone.
    if (isFollowupModalOpenGlobal.value && openFollowupContactId.value === contact.id) {
      lastDialerFollowupContactId = contact.id;
      return;
    }

    // Swap out an open modal only if it's the one the dialer opened for the
    // previous contact. Since the dialer runs in the background, an open modal
    // may instead be one the rep is filling in for someone else — replacing
    // it would throw their unsaved input away, so this one queues behind it
    // (App.vue shows it as soon as the rep closes theirs, with a toast). The
    // call window itself still pops up either way.
    const openIsDialerOwned =
      !!openFollowupContactId.value && openFollowupContactId.value === lastDialerFollowupContactId;
    openFollowupModal({
      contactId: contact.id,
      contactFname: contact.fname,
      contactLname: contact.lname,
      taskType: "call",
      replace: !isFollowupModalOpenGlobal.value || openIsDialerOwned,
    });
    lastDialerFollowupContactId = contact.id;
  }

  // The dialer's next call can arrive (dialer:next_call is pushed the moment
  // the backend auto-skips the previous one) before Twilio's own disconnect
  // for that previous call has reached the browser. Twillio.makeCall() refuses
  // to dial while it still considers a call active, which used to fail the
  // call and leave the contact stuck at "dialed" — so give the previous call a
  // moment to finish ending first.
  const PREVIOUS_CALL_END_WAIT_MS = 3000;

  async function waitForPreviousCallToEnd() {
    const deadline = Date.now() + PREVIOUS_CALL_END_WAIT_MS;
    while (Twillio.hasActiveCall() && Date.now() < deadline) {
      await new Promise((resolve) => setTimeout(resolve, 100));
    }
  }

  function onCallConnected(payload: any) {
    if (!payload?.dialerCallListContactId) return;
    if (payload.dialerCallListContactId !== currentDialerCallListContactId.value) return;
    markHumanConnected();
  }

  // Bumped on every placeCall(). Each call's event handlers capture the value
  // at the time it was placed and ignore their events once a newer call has
  // started — otherwise the previous call's late "disconnect" (see
  // waitForPreviousCallToEnd) would reset the new call's state and start an
  // outcome-wait timer that opens the wrap-up modal against the new contact.
  let callSeq = 0;

  async function placeCall(payload: any) {
    const seq = ++callSeq;
    const isCurrentCall = () => seq === callSeq;
    // Anything still pending belongs to the previous call, which the backend
    // has already resolved by the time it pushes this one.
    clearConnectFallback();
    clearOutcomeTimeout();

    currentQueueItem.value = payload;
    currentDialerCallListContactId.value = payload.dialerCallListContactId;
    to.value = payload.phone.number;
    isMuted.value = false;
    isHumanConnected.value = false;
    callStatus.value = "connecting";

    try {
      await waitForPreviousCallToEnd();
      await setPresence({ status: "on_call" });

      // Warm the backend's pre-call briefing cache while the number rings, so
      // enabling the assistant once someone answers doesn't wait on context
      // prep. Not awaited — dialing shouldn't be held up by it.
      if (sessionAIAssistantEnabled.value && payload.contact?.id) {
        // Clear anything left over from the previous contact. A prep still in
        // flight for a number that went unanswered is marked stale by this
        // and discards its result when it lands.
        aiAssistantOwnedByCall = false;
        await disableAIAssistant();
        prepareAIContext(payload.contact.id, sessionAIIcpId.value).catch((err) => {
          console.warn("[PowerDialer] AI context prep failed:", err);
        });
      }

      const result: any = await Twillio.makeCall(
        payload.phone.number,
        account.value.id,
        payload.contact.id,
        "",
        payload.organizationId,
        enableTranscription.value,
        enableAISummary.value,
        payload.dialerCallListContactId,
        // The session's Target ICP (set with or without the AI assistant) drives
        // the AI call summary.
        sessionAIIcpId.value || ""
      );

      const call: any = result.call;
      const callSid: string | undefined = result.callSid;
      currentCall.value = markRaw(call);
      sessionDialedCount.value++;
      if (callSid) {
        currentCallSid.value = callSid;
        parentCallSid.value = callSid;
        setOutboundCallSid(callSid);
      }

      call.on("ringing", () => {
        if (!isCurrentCall()) return;
        if (!isHumanConnected.value) callStatus.value = "ringing";
      });

      // With answerOnBridge, this fires once the contact's side picks up — but
      // that includes voicemail, so it isn't treated as "connected" on its own.
      // dialer:call_connected (human) is what opens the modals; this only arms
      // the fallback in case that push never arrives.
      call.on("accept", (conn: any) => {
        if (!isCurrentCall()) return;
        currentCallSid.value = conn.parameters.CallSid;
        parentCallSid.value = conn.parameters.CallSid;
        setOutboundCallSid(conn.parameters.CallSid);
        if (isHumanConnected.value) return;
        callStatus.value = "ringing";
        clearConnectFallback();
        connectFallbackId = setTimeout(() => {
          connectFallbackId = null;
          markHumanConnected();
        }, CONNECT_FALLBACK_MS);
      });

      call.on("disconnect", () => {
        // The browser leg ending doesn't tell us the outcome — voicemail,
        // no-answer, busy, and disconnected numbers all end the call exactly
        // like a real completed conversation from this client's point of view.
        // Only the backend webhook knows the real CallStatus/AMD result, so
        // just clear the call UI here and wait for the server to tell us
        // whether this needs a manual disposition (dialer:wrapup_needed) or
        // was already auto-skipped (dialer:auto_skipped).
        if (!isCurrentCall()) return;
        resetCallLocalState();
        const pendingId = currentDialerCallListContactId.value;
        awaitingCallOutcome.value = true;
        outcomeTimeoutId = setTimeout(() => {
          if (!awaitingCallOutcome.value) return;
          awaitingCallOutcome.value = false;
          outcomeTimeoutId = null;
          openWrapupModal(pendingId);
        }, OUTCOME_WAIT_TIMEOUT_MS);
      });

      call.on("reject", () => {
        if (!isCurrentCall()) return;
        $toast.info("Call was rejected");
        resetCallLocalStateAfterFailure();
      });

      call.on("cancel", () => {
        if (!isCurrentCall()) return;
        $toast.info("Call was canceled");
        resetCallLocalStateAfterFailure();
      });

      call.on("error", () => {
        if (!isCurrentCall()) return;
        $toast.error("Could not make call.");
        resetCallLocalStateAfterFailure();
      });

      dialerAutoAdvancePending.value = true;

      // Refresh the queue so the row's status/highlight reflects that this
      // contact is now being called, instead of only updating once the
      // wrap-up disposition is eventually saved.
      queryClient.invalidateQueries({ queryKey: ["dialerSession", "active"] });
    } catch (error: any) {
      console.error("Failed to place dialer call:", error);
      if (!isCurrentCall()) return;
      $toast.error(error?.message || "Failed to place call.");
      resetCallLocalStateAfterFailure();
    }
  }

  // Called either by the backend's "dialer:wrapup_needed" push (the normal
  // path for a real completed conversation) or by the local outcome-wait
  // timeout as a fallback if that push never arrives — guarded so a late
  // second trigger doesn't clobber a disposition the rep already started
  // filling in.
  function openWrapupModal(dialerCallListContactId: string | null | undefined) {
    if (!dialerCallListContactId) return;
    resetCallLocalState();
    queryClient.invalidateQueries({ queryKey: ["dialerSession", "active"] });
    // Reflects that the rep is no longer mid-call but hasn't asked for the next
    // one yet — distinct from "on_call" and "available". Fire-and-forget: the
    // modal opening doesn't need to wait on it.
    setPresence({ status: "wrap_up" }).catch((error) => {
      console.error("[PowerDialer] Failed to set wrap_up presence:", error);
    });
    if (wrapupTarget.value?.dialerCallListContactId === dialerCallListContactId) return;
    selectedDisposition.value = null;
    wrapupNotes.value = "";
    wrapupTarget.value = { dialerCallListContactId };
  }

  function onWrapupNeeded(payload: any) {
    clearOutcomeTimeout();
    openWrapupModal(payload.dialerCallListContactId);
  }

  // Fires when the backend already auto-dispositioned this call (voicemail,
  // no-answer, busy, or a disconnected number) and advanced the queue itself —
  // no manual wrap-up needed. The next call is already on its way via its own
  // dialer:next_call push; this just informs the rep and refreshes the queue.
  function onAutoSkipped(payload: any) {
    clearOutcomeTimeout();
    currentQueueItem.value = null;
    currentDialerCallListContactId.value = null;
    // The backend already advanced the queue itself (its own dialer:next_call
    // push is on the way) — clear this so the readyForAutoAdvance watcher
    // doesn't also fire a redundant requestNextCall() once any lingering
    // follow-up modal closes.
    dialerAutoAdvancePending.value = false;
    // The local outcome-wait timeout can lose the race and open the wrap-up
    // modal for this same call just before this auto-skip event arrives —
    // since the backend already disposed of it, close that modal instead of
    // leaving the rep stuck (it has no backdrop/Escape close).
    if (wrapupTarget.value?.dialerCallListContactId === payload.dialerCallListContactId) {
      wrapupTarget.value = null;
    }
    queryClient.invalidateQueries({ queryKey: ["dialerSession", "active"] });
    const label = AUTO_SKIP_REASON_LABELS[payload.reason] || payload.disposition || "Not answered";
    $toast.info(`${label} — moving to next call...`);
  }

  async function submitWrapupForm() {
    if (!wrapupTarget.value || !selectedDisposition.value) return;
    try {
      await submitWrapup({
        dialerCallListContactId: wrapupTarget.value.dialerCallListContactId,
        disposition: selectedDisposition.value.id,
        notes: wrapupNotes.value || undefined,
      });
      wrapupTarget.value = null;
      currentQueueItem.value = null;
      currentDialerCallListContactId.value = null;
      $toast.success("Call disposition saved.");
    } catch (error: any) {
      $toast.error(error?.response?.data?.message || "Failed to save disposition.");
    }
  }

  useDialerSocketListeners({
    onNextCall: placeCall,
    onCallConnected,
    onWrapupNeeded,
    onAutoSkipped,
  });

  // Closing/refreshing the tab kills the browser-based call and stops the
  // background dialer, so warn while a call is live or the session is running.
  const isDialerBusy = computed(
    () =>
      activeCallStates.includes(callStatus.value) ||
      (hasActiveSession.value && hasStarted.value && sessionStatus.value !== "paused")
  );

  const handleBeforeUnload = (e: BeforeUnloadEvent) => {
    if (!isDialerBusy.value) return;
    e.preventDefault();
    e.returnValue = "";
  };
  window.addEventListener("beforeunload", handleBeforeUnload);

  const controller = reactive({
    activeSession,
    isLoadingActiveSession,
    hasActiveSession,
    sessionStatus,
    hasStarted,
    displaySummary,
    canTriggerNextCall,
    currentDialerCallListContactId,
    currentContactName,
    wrapupTarget,
    selectedDisposition,
    wrapupNotes,
    showEndSessionConfirm,
    showSessionSummary,
    sessionSummaryStats,
    callStatus,
    showCallModal,
    to,
    isMuted,
    saveRecording,
    currentCallSid,
    parentCallSid,
    microphones,
    speakers,
    selectedMicId,
    selectedSpeakerId,
    formattedCallTimer,
    bannerCallDuration,
    isSettingPresence,
    isPausingSession,
    isResumingSession,
    isEndingSession,
    isSubmittingWrapup,
    requestNextCall,
    pauseSessionHandler,
    resumeSessionHandler,
    confirmEndSession,
    closeSessionSummary,
    submitWrapupForm,
    hangupCall,
    handleCloseCallModal,
    toggleRecording,
    toggleMute,
    handleTransfer,
    selectMicrophone,
    selectSpeaker,
    sendDigit,
  });

  currentPowerDialer.value = controller;

  onBeforeUnmount(() => {
    window.removeEventListener("beforeunload", handleBeforeUnload);
    teardownActiveCall();
    if (currentPowerDialer.value === controller) currentPowerDialer.value = null;
  });

  return controller;
}
